/* Karmagick v1.0.1 — le forme dell'altare delle stelle, copiate da reports/altare-2026-10-28/forme.js
   (dove si provano nella tavola). Le usa assets/kg-altare.js.
   v1.0.4 (2026-10-29): la forma «luna» è la luna sul mare nelle otto fasi; la usa anche la pagina della luna. */
'use strict';
/* L'altare delle stelle: le forme che la galassia prende, una per ogni cosa che Karmagick vende.
   Ogni forma è una funzione che, dato il numero di stelle, restituisce per ognuna posizione
   (x, y, z), luce, grandezza, tinta e animazione. Le coordinate stanno in un riquadro di 3,0 × 2,8
   centrato in 0 (y in alto): il motore poi lo scala e lo mette nello spazio libero accanto al testo.
   Animazioni (le fa lo shader): fumo che sale, fiamma che trema, anelli di suono della campana,
   scintille sui cristalli, grani della mala che scorrono uno alla volta, carta che ruota, chakra
   che si accendono dal basso verso l'alto. */
(() => {
  const T = { LUNA: 0, STELLA: 1, ORO: 2, TENUE: 3, ROSSO: 4, MARE: 5, FIAMMA: 6, CERA: 7, FUMO: 8, CRISTALLO: 9, BRONZO: 10, TERRACOTTA: 11, LEGNO: 12, AMETISTA: 13, ROSA: 14, AMBRA: 15, SALVIA: 16, SALE: 17, PIUMA: 18, TURCHESE: 19, CHAKRA: 20 };
  /* GOCCIA si gonfia sulla punta e cade; BRILLA respira piano; PIUMA oscilla attorno a dove è appesa */
  const A = { FERMO: 0, FUMO: 1, FIAMMA: 2, ONDA: 3, SCINTILLA: 4, GRANO: 5, CARTA: 6, CHAKRA: 7, GOCCIA: 8, BRILLA: 9, PIUMA: 10 };
  const caso = Math.random;
  const gauss = () => { let u = 0, v = 0; while (!u) u = caso(); while (!v) v = caso(); return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v); };
  const P = (o) => Object.assign({ x: 0, y: 0, z: 0, lum: 1, gr: 1, tinta: T.LUNA, anim: A.FERMO, fase: caso(), p1: 0, p2: 0 }, o);

  /* quante stelle a ogni parte, in proporzione */
  function quote(n, pesi) {
    const tot = pesi.reduce((s, p) => s + p, 0);
    const q = pesi.map((p) => Math.floor((n * p) / tot));
    q[0] += n - q.reduce((s, x) => s + x, 0);
    return q;
  }
  /* punti a caso dentro una regione: `dentro` dice se il punto c'è (e può passare dati a `fai`) */
  function riempi(n, [x0, y0, x1, y1], dentro, fai) {
    const out = [];
    let prove = 0;
    while (out.length < n && prove < n * 300 + 1000) {
      prove++;
      const x = x0 + caso() * (x1 - x0), y = y0 + caso() * (y1 - y0);
      const v = dentro(x, y);
      if (v) out.push(fai(x, y, v));
    }
    return out;
  }
  /* punti lungo una spezzata, distribuiti per lunghezza */
  function lungo(n, punti, fai) {
    const seg = [];
    let L = 0;
    for (let i = 1; i < punti.length; i++) {
      const [ax, ay] = punti[i - 1], [bx, by] = punti[i];
      const l = Math.hypot(bx - ax, by - ay);
      if (l > 0) { seg.push({ ax, ay, bx, by, l, da: L }); L += l; }
    }
    const out = [];
    for (let i = 0; i < n && seg.length; i++) {
      const s = ((i + caso()) / n) * L;
      let k = 0;
      while (k < seg.length - 1 && s > seg[k].da + seg[k].l) k++;
      const g = seg[k], v = (s - g.da) / g.l;
      out.push(fai(g.ax + (g.bx - g.ax) * v, g.ay + (g.by - g.ay) * v, s / L));
    }
    return out;
  }
  const arco = (cx, cy, rx, ry, a0, a1, passi = 72) => Array.from({ length: passi + 1 }, (_, i) => { const a = a0 + ((a1 - a0) * i) / passi; return [cx + Math.cos(a) * rx, cy + Math.sin(a) * ry]; });
  const tratto = (n, ax, ay, bx, by, spessore, fai) => lungo(n, [[ax, ay], [bx, by]], (x, y, t) => { const l = Math.hypot(bx - ax, by - ay) || 1; const s = gauss() * spessore; return fai(x - ((by - ay) / l) * s, y + ((bx - ax) / l) * s, t); });
  /* sposta e scala una forma (anche i parametri delle animazioni che sono misure) */
  function sposta(lista, s, dx, dy) {
    for (const p of lista) {
      /* goccia e piuma tengono in x, y lo scarto da un punto (la punta, l'aggancio) che sta nei parametri */
      const relativo = p.anim === A.GOCCIA || p.anim === A.PIUMA;
      p.x = p.x * s + (relativo ? 0 : dx); p.y = p.y * s + (relativo ? 0 : dy); p.z *= s;
      if (p.anim === A.FUMO) p.p1 *= s;
      else if (p.anim === A.FIAMMA || p.anim === A.GOCCIA) { p.p1 = p.p1 * s + dy; p.p2 *= s; }
      else if (p.anim === A.ONDA) p.p1 *= s;
      else if (p.anim === A.PIUMA) { p.p1 = p.p1 * s + dx; p.p2 = p.p2 * s + dy; }
    }
    return lista;
  }

  /* ── la candela: cilindro di cera, colature, stoppino, fiamma che trema e il suo alone ── */
  function candela(n) {
    const R = 0.34, TOP = 0.3, BASE = -1.22, RY = 0.075;
    const [nCorpo, nBordo, nPozza, nGocce, nStoppino, nFiamma, nAlone] = quote(n, [0.5, 0.07, 0.03, 0.04, 0.01, 0.2, 0.15]);
    const out = [];
    out.push(...riempi(nCorpo, [-R, BASE, R, TOP], (x, y) => y < TOP - RY * Math.sqrt(Math.max(0, 1 - (x / R) ** 2)), (x, y) => {
      const c = Math.sqrt(Math.max(0, 1 - (x / R) ** 2));
      const alto = (y - BASE) / (TOP - BASE);
      const riflesso = Math.exp(-(((x + R * 0.4) / (R * 0.16)) ** 2));
      return P({ x, y, z: c * 0.15, lum: 0.24 + 0.6 * c * (0.35 + 0.65 * alto) + 0.45 * riflesso * (0.3 + 0.7 * alto), gr: 0.9, tinta: T.CERA });
    }));
    out.push(...lungo(nBordo, arco(0, TOP, R, RY, 0, Math.PI * 2, 120), (x, y) => P({ x, y, z: 0.1, lum: y < TOP ? 0.95 : 0.55, gr: 0.85, tinta: T.CERA })));
    out.push(...riempi(nPozza, [-R, TOP - RY, R, TOP + RY], (x, y) => (x / (R * 0.82)) ** 2 + ((y - TOP) / (RY * 0.8)) ** 2 < 1, (x, y) => P({ x, y, z: 0.1, lum: 0.35 + 0.6 * Math.exp(-((x / 0.1) ** 2)), gr: 0.8, tinta: T.FIAMMA })));
    [[0.21, 0.34], [-0.25, 0.52]].forEach(([gx, lung]) => out.push(...riempi(Math.round(nGocce / 2), [gx - 0.06, TOP - lung - 0.03, gx + 0.06, TOP], (x, y) => {
      const v = (TOP - y) / lung;
      const w = v > 1 ? 0.035 * Math.sqrt(Math.max(0, 1 - ((v - 1) / 0.09) ** 2)) : 0.028 * (1 - v * 0.35);
      return Math.abs(x - gx) < w;
    }, (x, y) => P({ x, y, z: 0.17, lum: 0.8, gr: 0.85, tinta: T.CERA }))));
    out.push(...riempi(nStoppino, [-0.012, TOP - 0.01, 0.012, TOP + 0.14], () => true, (x, y) => P({ x, y, z: 0.1, lum: 0.3, gr: 0.7, tinta: T.LEGNO })));
    const F0 = TOP + 0.1, FH = 0.62;
    out.push(...riempi(nFiamma, [-0.14, F0, 0.14, F0 + FH], (x, y) => {
      const u = (y - F0) / FH;
      const w = 0.12 * Math.pow(Math.sin(Math.PI * Math.pow(u, 0.62)), 0.85);
      return Math.abs(x) < w ? { u, w } : null;
    }, (x, y, { u, w }) => P({ x, y, z: 0.2, lum: 0.9 + 1.3 * (1 - Math.abs(x) / w) * (1 - u * 0.55), gr: 1.0, tinta: T.FIAMMA, anim: A.FIAMMA, p1: F0, p2: FH })));
    for (let i = 0; i < nAlone; i++) {
      const a = caso() * 6.283, r = Math.abs(gauss()) * 0.3;
      out.push(P({ x: Math.cos(a) * r, y: F0 + 0.24 + Math.sin(a) * r * 1.15, z: 0.2, lum: 0.1, gr: 1.6, tinta: T.FIAMMA, anim: A.FIAMMA, p1: F0, p2: FH }));
    }
    return out;
  }

  /* ── il cono d'incenso sul piattino d'ottone, con la brace e il filo di fumo ── */
  function incenso(n) {
    const PY = -0.98, PRX = 0.62, PRY = 0.13, PUNTA = -0.3, BASE_C = PY + 0.02, HW = 0.2;
    const [nPiatto, nBordo, nCono, nBrace, nFumo] = quote(n, [0.17, 0.07, 0.25, 0.02, 0.49]);
    const out = [];
    out.push(...riempi(nPiatto, [-PRX, PY - PRY - 0.08, PRX, PY + PRY], (x, y) => {
      const e = (x / PRX) ** 2 + ((y - PY) / PRY) ** 2;
      if (e < 1) return { e };
      const fronte = PY - PRY * Math.sqrt(Math.max(0, 1 - (x / PRX) ** 2));
      return y < fronte && y > fronte - 0.07 ? { lato: true } : null;
    }, (x, y, v) => P({ x, y, z: -0.1, lum: v.lato ? 0.5 - 0.3 * Math.abs(x / PRX) : 0.16 + 0.22 * (1 - v.e), gr: 0.85, tinta: T.BRONZO })));
    out.push(...lungo(nBordo, arco(0, PY, PRX, PRY, 0, Math.PI * 2, 120), (x, y) => P({ x, y, z: -0.08, lum: y < PY ? 1.0 : 0.6, gr: 0.85, tinta: T.BRONZO })));
    out.push(...riempi(nCono, [-HW, BASE_C, HW, PUNTA], (x, y) => {
      const v = (y - BASE_C) / (PUNTA - BASE_C);
      const hw = HW * (1 - v);
      return Math.abs(x) < hw ? { v, hw } : null;
    }, (x, y, { v, hw }) => P({ x, y, z: 0.05, lum: (0.28 + 0.5 * (1 - (x / hw + 1) / 2)) * (1 - v * 0.25), gr: 0.95, tinta: v > 0.86 ? T.TENUE : T.TERRACOTTA })));
    for (let i = 0; i < nBrace; i++) out.push(P({ x: gauss() * 0.012, y: PUNTA - 0.02 + gauss() * 0.012, z: 0.06, lum: 2.2, gr: 1.1, tinta: T.FIAMMA, anim: A.FIAMMA, p1: PUNTA - 0.05, p2: 0.06 }));
    for (let i = 0; i < nFumo; i++) out.push(P({ x: gauss() * 0.004, y: PUNTA + 0.01, z: 0.05, lum: 0.5, gr: 1.0, tinta: T.FUMO, anim: A.FUMO, p1: 1.72 }));
    return out;
  }

  /* ── la drusa di quarzo: punte esagonali con una faccia in luce e una in ombra ── */
  const PUNTE = [[0, 0, 1.95, 0.36, 0.1], [-0.45, -20, 1.35, 0.3, 0.0], [0.47, 17, 1.5, 0.3, 0.05], [-0.82, -38, 0.75, 0.22, -0.05], [0.84, 36, 0.8, 0.22, -0.05], [-0.2, -9, 0.95, 0.24, 0.22], [0.25, 11, 0.82, 0.2, 0.24]];
  function puntaInterna(p, u, v) {
    const [, , h, w] = p, tip = w * 0.95;
    if (v < 0 || v > h) return false;
    return v < h - tip ? Math.abs(u) < w / 2 : Math.abs(u) < (w / 2) * ((h - v) / tip);
  }
  function cristallo(n) {
    const BASE = -0.9;
    const [nRoccia, nPunte, nScintille] = quote(n, [0.15, 0.81, 0.04]);
    const out = [];
    out.push(...riempi(nRoccia, [-1.0, -1.2, 1.0, -0.78], (x, y) => {
      const sopra = -0.84 + 0.05 * Math.sin(x * 7) - 0.07 * x * x, sotto = -1.15 + 0.1 * x * x;
      return y < sopra && y > sotto && Math.abs(x) < 0.98;
    }, (x, y) => P({ x, y, z: -0.05, lum: 0.12 + 0.14 * caso(), gr: 0.9, tinta: T.TENUE })));
    const aree = PUNTE.map(([, , h, w]) => h * w);
    const totale = aree.reduce((s, a) => s + a, 0);
    PUNTE.forEach((p, j) => {
      const [cx, gradi, h, w, pz] = p;
      const a = (gradi * Math.PI) / 180, ca = Math.cos(a), sa = Math.sin(a);
      const k = Math.round((nPunte * aree[j]) / totale);
      for (let i = 0; i < k; i++) {
        let u, v;
        do { u = (caso() - 0.5) * w; v = caso() * h; } while (!puntaInterna(p, u, v));
        const luce = u < 0 ? 0.85 : 0.36;
        const spigolo = 0.6 * Math.exp(-((u / (w * 0.035)) ** 2));
        const lum = (luce + spigolo) * (0.62 + 0.38 * (v / h)) * 1.15;
        out.push(P({ x: cx + u * ca + v * sa, y: BASE - u * sa + v * ca, z: pz - u * 0.4, lum, gr: 0.9, tinta: T.CRISTALLO }));
      }
    });
    while (out.length < nRoccia + nPunte) out.push({ ...out[(caso() * out.length) | 0] });
    for (let i = 0; i < nScintille; i++) {
      const p = PUNTE[(caso() * 3) | 0], [cx, gradi, h, w, pz] = p;
      const a = (gradi * Math.PI) / 180, v = h * (0.75 + caso() * 0.25), u = (caso() - 0.5) * w * 0.3;
      out.push(P({ x: cx + u * Math.cos(a) + v * Math.sin(a), y: BASE - u * Math.sin(a) + v * Math.cos(a), z: pz + 0.1, lum: 1.2, gr: 1.3, tinta: T.CRISTALLO, anim: A.SCINTILLA }));
    }
    return out;
  }

  /* ── la campana tibetana sul suo cuscino, col bastone, e gli anelli del suono ── */
  function campana(n) {
    const CY = 0.02, RX = 0.95, RY = 0.2, FONDO = -0.7;
    const [nCorpo, nBordo, nInterno, nBastone, nCuscino, nOnde] = quote(n, [0.33, 0.08, 0.07, 0.05, 0.1, 0.37]);
    const fronte = (x) => CY - RY * Math.sqrt(Math.max(0, 1 - (x / RX) ** 2));
    const fondo = (x) => FONDO + 0.5 * (x / RX) ** 4 + 0.12 * (x / RX) ** 2;
    const out = [];
    out.push(...riempi(nCorpo, [-RX, FONDO, RX, CY], (x, y) => y < fronte(x) && y > fondo(x), (x, y) => {
      const c = Math.sqrt(Math.max(0, 1 - (x / RX) ** 2));
      const riflesso = Math.exp(-(((x + 0.36) / 0.11) ** 2));
      const fasce = Math.exp(-(((y - (CY - 0.34)) / 0.012) ** 2)) + Math.exp(-(((y - (CY - 0.39)) / 0.008) ** 2));
      return P({ x, y, z: c * 0.25, lum: 0.16 + 0.32 * c + 0.6 * riflesso + 0.4 * fasce, gr: 0.9, tinta: T.BRONZO });
    }));
    out.push(...lungo(nBordo, arco(0, CY, RX, RY, 0, Math.PI * 2, 160), (x, y) => P({ x, y, z: 0.05, lum: y < CY ? 1.1 : 0.6, gr: 0.85, tinta: T.BRONZO })));
    out.push(...riempi(nInterno, [-RX, CY - RY, RX, CY + RY], (x, y) => (x / RX) ** 2 + ((y - CY) / RY) ** 2 < 0.9, (x, y) => P({ x, y, z: 0, lum: y > CY ? 0.3 : 0.12, gr: 0.85, tinta: T.BRONZO })));
    out.push(...tratto(Math.round(nBastone * 0.7), 0.74, 0.28, 1.34, 0.96, 0.012, (x, y) => P({ x, y, z: 0.3, lum: 0.55, gr: 0.85, tinta: T.LEGNO })));
    out.push(...tratto(nBastone - Math.round(nBastone * 0.7), 0.64, 0.17, 0.76, 0.3, 0.03, (x, y) => P({ x, y, z: 0.3, lum: 0.6, gr: 0.9, tinta: T.TERRACOTTA })));
    out.push(...riempi(nCuscino, [-0.9, -0.98, 0.9, -0.62], (x, y) => (x / 0.88) ** 2 + ((y - -0.8) / 0.16) ** 2 < 1 && y < fondo(x) + 0.02, (x, y) => P({ x, y, z: -0.1, lum: 0.18 + 0.22 * (1 - Math.abs(x) / 0.88), gr: 0.9, tinta: T.AMETISTA })));
    for (let i = 0; i < nOnde; i++) out.push(P({ x: 0, y: CY, z: 0, lum: 0.62, gr: 0.9, tinta: T.ORO, anim: A.ONDA, fase: caso(), p1: RX, p2: (i % 3) / 3 + caso() * 0.04 }));
    return out;
  }

  /* ── la carta dei tarocchi XVIII, la Luna: cornice, luna coi raggi, gocce, torri, sentiero, acqua ── */
  function tarocco(n) {
    const W = 0.78, H = 1.3, R = 0.08;
    const [nCornice, nInterna, nLuna, nRaggi, nGocce, nTorri, nSentiero, nAcqua, nMonti, nNumero] = quote(n, [0.14, 0.06, 0.2, 0.1, 0.04, 0.13, 0.08, 0.09, 0.06, 0.09]);
    const out = [];
    const C = (o) => P({ anim: A.CARTA, ...o });
    const rett = (w, h, r) => [
      ...arco(w - r, h - r, r, r, 0, Math.PI / 2, 8), ...arco(-w + r, h - r, r, r, Math.PI / 2, Math.PI, 8),
      ...arco(-w + r, -h + r, r, r, Math.PI, Math.PI * 1.5, 8), ...arco(w - r, -h + r, r, r, Math.PI * 1.5, Math.PI * 2, 8), [w, h - r],
    ];
    out.push(...lungo(nCornice, rett(W, H, R), (x, y) => C({ x: x + gauss() * 0.004, y: y + gauss() * 0.004, lum: 0.95, gr: 0.9, tinta: T.ORO })));
    out.push(...lungo(nInterna, rett(W - 0.08, H - 0.08, 0.03), (x, y) => C({ x, y, lum: 0.42, gr: 0.75, tinta: T.ORO })));
    /* la luna: un disco con dentro la falce (come nella carta), più chiara la falce */
    const LY = 0.6, LR = 0.3;
    out.push(...riempi(nLuna, [-LR, LY - LR, LR, LY + LR], (x, y) => x * x + (y - LY) ** 2 < LR * LR, (x, y) => {
      const falce = (x + 0.06) ** 2 + (y - LY) ** 2 > (LR * 0.78) ** 2 || x < -0.02;
      return C({ x, y, z: 0.05, lum: falce ? 1.05 : 0.3, gr: 0.9, tinta: T.LUNA });
    }));
    for (let i = 0; i < 32; i++) {
      const a = (i / 32) * Math.PI * 2, lunga = i % 2 === 0;
      out.push(...tratto(Math.round(nRaggi / 32), Math.cos(a) * (LR + 0.04), LY + Math.sin(a) * (LR + 0.04), Math.cos(a) * (LR + (lunga ? 0.26 : 0.14)), LY + Math.sin(a) * (LR + (lunga ? 0.26 : 0.14)), 0.004, (x, y) => C({ x, y, lum: 0.6, gr: 0.75, tinta: T.ORO })));
    }
    /* le gocce di rugiada che cadono dalla luna */
    for (let i = 0; i < 15; i++) {
      const gx = -0.5 + (i / 14) * 1.0 + (caso() - 0.5) * 0.05, gy = 0.14 + Math.sin(i * 1.7) * 0.06;
      for (let j = 0; j < Math.round(nGocce / 15); j++) { const v = caso(); out.push(C({ x: gx + gauss() * 0.008 * (1 - v), y: gy + v * 0.05, lum: 0.75, gr: 0.75, tinta: T.ORO })); }
    }
    /* le due torri, con i merli e una finestra */
    [[-0.52, 1], [0.52, -1]].forEach(([tx]) => {
      const tw = 0.1, y0 = -0.26, y1 = 0.12;
      const sagoma = [[tx - tw, y0], [tx - tw, y1], [tx - tw, y1 + 0.05], [tx - tw * 0.45, y1 + 0.05], [tx - tw * 0.45, y1], [tx + tw * 0.1, y1], [tx + tw * 0.1, y1 + 0.05], [tx + tw * 0.55, y1 + 0.05], [tx + tw * 0.55, y1], [tx + tw, y1], [tx + tw, y1 + 0.05], [tx + tw, y0]];
      out.push(...lungo(Math.round(nTorri * 0.55), sagoma, (x, y) => C({ x, y, lum: 0.75, gr: 0.8, tinta: T.STELLA })));
      out.push(...riempi(Math.round(nTorri * 0.2), [tx - tw, y0, tx + tw, y1], (x, y) => !(Math.abs(x - tx) < 0.025 && y > 0.0 && y < 0.07), (x, y) => C({ x, y, lum: 0.18, gr: 0.75, tinta: T.STELLA })));
    });
    /* il sentiero che sale dall'acqua fino ai monti */
    const sent = (lato) => Array.from({ length: 40 }, (_, i) => { const v = i / 39; const y = -0.78 + v * 0.62; return [Math.sin(v * 7.5) * 0.14 * (1 - v * 0.6) + lato * (0.07 * (1 - v) + 0.008), y]; });
    out.push(...lungo(Math.round(nSentiero / 2), sent(-1), (x, y) => C({ x, y, lum: 0.55, gr: 0.75, tinta: T.ORO })));
    out.push(...lungo(nSentiero - Math.round(nSentiero / 2), sent(1), (x, y) => C({ x, y, lum: 0.55, gr: 0.75, tinta: T.ORO })));
    /* l'acqua in basso: onde orizzontali */
    for (let r = 0; r < 4; r++) {
      const y0 = -0.86 - r * 0.09;
      out.push(...lungo(Math.round(nAcqua / 4), Array.from({ length: 50 }, (_, i) => { const x = -W + 0.12 + (i / 49) * (2 * W - 0.24); return [x, y0 + Math.sin(x * 22 + r) * 0.012]; }), (x, y) => C({ x, y, lum: 0.5 - r * 0.07, gr: 0.75, tinta: T.STELLA })));
    }
    /* i monti all'orizzonte */
    out.push(...lungo(nMonti, [[-W + 0.1, -0.16], [-0.32, -0.04], [-0.12, -0.14], [0.08, 0.0], [0.3, -0.12], [W - 0.1, -0.06]], (x, y) => C({ x, y, lum: 0.38, gr: 0.75, tinta: T.TENUE })));
    /* il numero: XVIII, in alto */
    const lettere = [];
    const lw = 0.05, lh = 0.075, sp = 0.035, Y = H - 0.18;
    const larghezze = [lw * 2, lw * 2, 0.002, 0.002, 0.002];
    let x = -(larghezze.reduce((s, l) => s + l, 0) + sp * 4) / 2;
    ['X', 'V', 'I', 'I', 'I'].forEach((c, i) => {
      if (c === 'X') lettere.push([[x, Y - lh], [x + lw * 2, Y + lh]], [[x, Y + lh], [x + lw * 2, Y - lh]]);
      else if (c === 'V') lettere.push([[x, Y + lh], [x + lw, Y - lh]], [[x + lw, Y - lh], [x + lw * 2, Y + lh]]);
      else lettere.push([[x, Y - lh], [x, Y + lh]]);
      x += larghezze[i] + sp;
    });
    lettere.forEach(([a, b]) => out.push(...tratto(Math.round(nNumero / lettere.length), a[0], a[1], b[0], b[1], 0.003, (px, py) => C({ x: px, y: py, lum: 0.9, gr: 0.75, tinta: T.ORO }))));
    return out;
  }

  /* ── la mala da 108 grani (come quelle del negozio: quarzo rosa, giada, corniola, cristallo di
     rocca), il guru e la nappa ── */
  function mala(n) {
    const NG = 108, R = 1.0, CY = 0.12, RG = 0.027;
    const [nGrani, nGuru, nNappa, nFilo] = quote(n, [0.72, 0.06, 0.18, 0.04]);
    const out = [];
    for (let i = 0; i < nGrani; i++) {
      const j = i % NG, a = -Math.PI / 2 + ((j + 0.5) / NG) * Math.PI * 2;
      const r = Math.sqrt(caso()) * RG, b = caso() * 6.283, ox = Math.cos(b) * r, oy = Math.sin(b) * r;
      const luce = 0.35 + 0.8 * Math.max(0, (-ox * 0.6 + oy * 0.8) / RG) * 0.9 + 0.15;
      out.push(P({ x: ox, y: oy, z: 0.1, lum: luce * 0.75, gr: 0.75, tinta: T.ROSA, anim: A.GRANO, p1: a, p2: R }));
    }
    const GY = CY - R * 1.08 - 0.15, GR = 0.085;
    for (let i = 0; i < nGuru; i++) {
      const r = Math.sqrt(caso()) * GR, b = caso() * 6.283, ox = Math.cos(b) * r, oy = Math.sin(b) * r;
      out.push(P({ x: ox, y: GY + oy, z: 0.12, lum: (0.45 + 0.9 * Math.max(0, (-ox * 0.6 + oy * 0.8) / GR)) * 0.8, gr: 0.9, tinta: T.ROSA }));
    }
    out.push(...tratto(nFilo, 0, CY - R * 1.08, 0, GY + GR, 0.004, (x, y) => P({ x, y, lum: 0.5, gr: 0.75, tinta: T.ORO })));
    /* la nappa: un cappuccio e i fili che si aprono a ventaglio */
    const N0 = GY - GR - 0.02;
    const nCapp = Math.round(nNappa * 0.2);
    out.push(...riempi(nCapp, [-0.07, N0 - 0.12, 0.07, N0], (x, y) => Math.abs(x) < 0.03 + 0.04 * ((N0 - y) / 0.12), (x, y) => P({ x, y, z: 0.1, lum: 0.6, gr: 0.85, tinta: T.TERRACOTTA })));
    for (let f = 0; f < 16; f++) {
      const v = (f / 15 - 0.5) * 2;
      out.push(...tratto(Math.round((nNappa - nCapp) / 16), v * 0.05, N0 - 0.12, v * 0.13, N0 - 0.45 - Math.abs(v) * 0.02, 0.003, (x, y) => P({ x, y, z: 0.1, lum: 0.55, gr: 0.8, tinta: T.TERRACOTTA })));
    }
    return out;
  }

  /* ── il tappetino: una figura seduta a gambe incrociate sul tappetino, e i sette chakra ── */
  const CHAKRA_Y = [-0.5, -0.3, -0.05, 0.3, 0.72, 1.0, 1.24];
  function inFigura(x, y) {
    const cerchio = (cx, cy, r) => (x - cx) ** 2 + (y - cy) ** 2 < r * r;
    const capsula = (ax, ay, bx, by, r) => {
      const dx = bx - ax, dy = by - ay, t = Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / (dx * dx + dy * dy)));
      return (x - ax - dx * t) ** 2 + (y - ay - dy * t) ** 2 < r * r;
    };
    if (cerchio(0, 1.0, 0.17)) return true; // testa
    if (Math.abs(x) < 0.065 && y > 0.74 && y < 0.88) return true; // collo
    const v = (0.72 - y) / 1.0; // busto: dalle spalle alla vita
    if (v >= 0 && v <= 1 && Math.abs(x) < 0.37 - 0.07 * v - 0.05 * Math.max(0, 0.15 - v) * 4) return true;
    for (const s of [-1, 1]) {
      if (capsula(s * 0.33, 0.64, s * 0.55, 0.03, 0.065)) return true; // braccio
      if (capsula(s * 0.55, 0.03, s * 0.74, -0.46, 0.055)) return true; // avambraccio
      if (cerchio(s * 0.76, -0.5, 0.06)) return true; // mano sul ginocchio
      if (capsula(s * 0.05, -0.45, s * 0.92, -0.58, 0.13)) return true; // coscia
      if (capsula(s * 0.86, -0.66, s * -0.25, -0.72, 0.09)) return true; // stinco incrociato
    }
    return false;
  }
  function tappetino(n) {
    const [nTappeto, nFigura, nContorno, nChakra, nAura] = quote(n, [0.14, 0.36, 0.17, 0.21, 0.12]);
    const out = [];
    const fronte = -1.22, retro = -0.96;
    out.push(...riempi(nTappeto, [-1.3, fronte, 1.3, retro], (x, y) => { const v = (y - fronte) / (retro - fronte); return Math.abs(x) < 1.3 - 0.25 * v; }, (x, y) => {
      const v = (y - fronte) / (retro - fronte), bordo = 1.3 - 0.25 * v - Math.abs(x) < 0.025 || v < 0.06 || v > 0.94;
      return P({ x, y, z: -0.2 + v * 0.1, lum: bordo ? 0.6 : 0.14, gr: 0.85, tinta: T.TERRACOTTA });
    }));
    out.push(...riempi(nFigura, [-1.05, -0.85, 1.05, 1.2], inFigura, (x, y) => P({ x, y, z: 0.05, lum: 0.2 + 0.06 * caso(), gr: 0.85, tinta: T.STELLA })));
    out.push(...riempi(nContorno, [-1.05, -0.85, 1.05, 1.2], (x, y) => {
      if (!inFigura(x, y)) return false;
      for (let k = 0; k < 8; k++) { const a = (k / 8) * 6.283; if (!inFigura(x + Math.cos(a) * 0.025, y + Math.sin(a) * 0.025)) return true; }
      return false;
    }, (x, y) => P({ x, y, z: 0.06, lum: 0.62, gr: 0.85, tinta: T.STELLA })));
    for (let i = 0; i < nChakra; i++) {
      const c = i % 7, s = c === 6 ? 0.05 : 0.032;
      out.push(P({ x: gauss() * s, y: CHAKRA_Y[c] + gauss() * s, z: 0.15, lum: 1.25, gr: 1.25, tinta: T.CHAKRA + c, anim: A.CHAKRA, p1: c }));
    }
    for (let i = 0; i < nAura; i++) {
      const a = caso() * 6.283, r = 1.0 + gauss() * 0.06;
      out.push(P({ x: Math.cos(a) * r * 0.82, y: 0.25 + Math.sin(a) * r * 1.05, z: -0.1, lum: 0.12, gr: 0.9, tinta: T.ORO }));
    }
    return out;
  }

  /* ── la luna di stanotte ──
     La fase viene dal cielo vero (assets/kg-cielo.js, caricato prima di questo file), così l'apertura dice
     la stessa fase della sezione della luna e della pagina; senza, dal mese sinodico medio. */
  const SINODICO = 29.530588853, LUNA_NUOVA = Date.UTC(2000, 0, 6, 18, 14);
  function faseDi(data) {
    const giorni = (data.getTime() - LUNA_NUOVA) / 86400000;
    const eta = ((giorni % SINODICO) + SINODICO) % SINODICO;
    /* i nomi (e le chiavi) della sezione «La luna di stanotte» del sito */
    const fasi = [[1, 'new', 'Luna nuova'], [6.4, 'waxing_crescent', 'Luna crescente'], [8.4, 'first_quarter', 'Primo quarto'], [13.8, 'waxing_gibbous', 'Gibbosa crescente'], [15.8, 'full', 'Luna piena'], [21.1, 'waning_gibbous', 'Gibbosa calante'], [23.1, 'last_quarter', 'Ultimo quarto'], [28.53, 'waning_crescent', 'Luna calante'], [99, 'new', 'Luna nuova']];
    const [, chiave, nome] = fasi.find(([fine]) => eta < fine);
    return { eta, angolo: (eta / SINODICO) * Math.PI * 2, chiave, nome };
  }
  const OGGI = (() => {
    const C = window.KG_CIELO;
    if (!C) return faseDi(new Date());
    const l = C.luna(new Date());
    return { eta: l.eta, angolo: l.angolo, chiave: l.chiave, nome: l.chiave };
  })();
  const MARI = [[-0.58, 0.12, 0.32], [-0.45, -0.12, 0.24], [-0.28, 0.48, 0.24], [0.24, 0.47, 0.15], [0.38, 0.18, 0.17], [0.78, 0.3, 0.1], [0.62, -0.12, 0.13], [0.42, -0.28, 0.09], [-0.2, -0.36, 0.16], [-0.55, -0.4, 0.09], [-0.25, 0.78, 0.09], [0.05, 0.8, 0.09], [0.35, 0.75, 0.08], [0.05, 0.3, 0.07]];
  /* Le otto fasi come si riconoscono a colpo d'occhio. L'età (in giorni) di ognuna è scelta perché la
     sagoma sia netta: la falce ha in luce il 22% del disco, il quarto il 50%, la gibbosa il 78%. La
     vetrina mostra quella di stanotte; la pagina della luna quella del giorno scelto.
     Dalla tavola delle forme (reports/forme-vetrina-2026-10-29/proposte.js), scelta dall'utente il 2026-10-29. */
  const FASI_OTTO = [['new', 0], ['waxing_crescent', 4.59], ['first_quarter', 7.38], ['waxing_gibbous', 10.18], ['full', 14.77], ['waning_gibbous', 19.35], ['last_quarter', 22.15], ['waning_crescent', 24.94]];
  const indiceFase = (chiave) => Math.max(0, FASI_OTTO.findIndex(([k]) => k === chiave));
  const faseVetrina = () => indiceFase(OGGI.chiave);
  /* una delle otto lune: la parte in luce piena e nitida fino al terminatore; quella in ombra resta
     disegnata solo dal suo bordo e da un velo di luce cinerea, così la fase si legge anche da quasi piena.
     La luna nuova è un disco scuro col bordo acceso. */
  function lunaOtto(n, cx, cy, r, k) {
    const ang = (FASI_OTTO[k][1] / SINODICO) * Math.PI * 2, s = [Math.sin(ang), -Math.cos(ang)];
    const nuova = k === 0;
    const [nLuce, nBordo, nCenere] = quote(n, nuova ? [0, 0.7, 0.3] : [0.86, 0.08, 0.06]);
    const out = [];
    let prove = 0;
    while (out.length < nLuce && prove++ < nLuce * 60) {
      const x = caso() * 2 - 1, y = caso() * 2 - 1, d = x * x + y * y;
      if (d > 1) continue;
      const luce = x * s[0] + Math.sqrt(1 - d) * s[1];
      if (luce <= 0) continue;
      let mare = 0;
      for (const [mx, my, mr] of MARI) mare = Math.max(mare, Math.exp((-((x - mx) ** 2 + (y - my) ** 2) / (mr * mr)) * 1.4));
      if (caso() > 1 - 0.45 * mare) continue;
      const terminatore = Math.min(1, luce * 7);
      out.push(P({ x: cx + x * r, y: cy + y * r, z: 0.1, lum: (0.5 + 0.5 * terminatore) * (1 - 0.48 * mare) * (0.85 + 0.3 * Math.sqrt(1 - d)), gr: 0.88, tinta: mare > 0.5 ? T.MARE : T.LUNA }));
    }
    /* il bordo della parte in ombra */
    for (let i = 0; i < nBordo; i++) {
      const a = caso() * 6.283, x = Math.cos(a), y = Math.sin(a);
      if (!nuova && x * s[0] > -0.02) continue;
      const rr = 1 + gauss() * 0.012;
      out.push(P({ x: cx + x * r * rr, y: cy + y * r * rr, z: 0.08, lum: nuova ? 0.55 : 0.22, gr: 0.85, tinta: T.LUNA, anim: nuova ? A.SCINTILLA : A.FERMO }));
    }
    /* la luce cinerea: un velo appena visibile sulla parte in ombra (alla luna nuova anche un alone attorno) */
    for (let i = 0; i < nCenere; i++) {
      const a = caso() * 6.283, q = nuova && caso() < 0.4 ? 1.04 + Math.abs(gauss()) * 0.08 : Math.sqrt(caso()), x = Math.cos(a) * q, y = Math.sin(a) * q;
      if (q <= 1 && x * s[0] + Math.sqrt(Math.max(0, 1 - q * q)) * s[1] > 0) continue;
      out.push(P({ x: cx + x * r, y: cy + y * r, z: 0.05, lum: q > 1 ? 0.1 : 0.07, gr: 0.85, tinta: T.TENUE }));
    }
    return { punti: out, luce: (1 - Math.cos(ang)) / 2, verso: s[0] };
  }
  /* la luna sul mare: la luna in una delle otto fasi (k; di base quella di stanotte) e la sua scia che
     trema sull'acqua */
  const LUNA_MARE = { x: 0, y: 0.55, r: 0.62 };
  function lunaAcqua(n, k = faseVetrina()) {
    const [nLuna, nScia, nOrizzonte, nOnde] = quote(n, [0.6, 0.18, 0.05, 0.17]);
    const out = [];
    const OR = -0.32, R = LUNA_MARE.r;
    const luna = lunaOtto(nLuna, LUNA_MARE.x, LUNA_MARE.y, R, k);
    out.push(...luna.punti);
    out.push(...lungo(nOrizzonte, [[-1.45, OR], [1.45, OR]], (x, y) => P({ x, y: y + gauss() * 0.004, z: 0, lum: 0.3, gr: 0.75, tinta: T.STELLA })));
    /* la scia: righe d'acqua spezzate in trattini, più larghe verso chi guarda, che scintillano. È forte
       quanto è piena la luna e cade sotto la parte in luce (a destra quando cresce, a sinistra quando cala) */
    const piena = 0.1 + 0.9 * luna.luce, scarto = luna.verso * R * 0.4;
    const trattini = [];
    for (let j = 0; j < 20; j++) {
      const v = j / 19, y = OR - 0.05 - Math.pow(v, 1.2) * 1.0, larga = 0.08 + v * 0.55 * (0.45 + 0.55 * luna.luce);
      for (let d = 0; d < 2 + Math.round(v * 5); d++) { const c = scarto * (1 - v * 0.4) + gauss() * larga * 0.42, l = 0.025 + caso() * (0.04 + 0.08 * v); trattini.push({ a: [c - l, y], b: [c + l, y], v, luce: caso() }); }
    }
    const Lt = trattini.reduce((t, d) => t + (d.b[0] - d.a[0]), 0);
    for (const d of trattini) out.push(...lungo(Math.round((nScia * (d.b[0] - d.a[0])) / Lt), [d.a, d.b], (x, y) => P({ x, y: y + gauss() * 0.003, z: 0.1, lum: (0.25 + 0.6 * (1 - d.v)) * piena * (0.6 + 0.8 * d.luce), gr: 0.85, tinta: T.LUNA, anim: d.luce > 0.75 ? A.SCINTILLA : A.FERMO })));
    for (let r = 0; r < 6; r++) out.push(...lungo(Math.round(nOnde / 6), Array.from({ length: 40 }, (_, i) => { const x = -1.45 + (i / 39) * 2.9; return [x, OR - 0.12 - r * 0.17 + Math.sin(x * 9 + r * 2) * 0.01]; }), (x, y) => P({ x, y, z: 0, lum: 0.12 - r * 0.012, gr: 0.75, tinta: T.MARE })));
    return out;
  }
  /* La luna sul mare a tutto sfondo (pagina della luna): le stesse parti di lunaAcqua, ma misurate sulla
     pagina. o = { mx, my, r } è il disco, or l'orizzonte, fondo il bordo basso, x0 e x1 i bordi di lato,
     tutto nelle coordinate del cielo (y in alto). L'orizzonte va da un bordo all'altro e sfuma ai lati; le
     onde scendono fino in fondo, più fitte verso l'orizzonte (prospettiva); la scia parte sotto la parte in
     luce e si allarga verso chi guarda. */
  function scenaMare(n, k, o) {
    const { mx, my, r, or, fondo, x0, x1 } = o;
    const [nLuna, nScia, nOrizzonte, nOnde] = quote(n, [0.54, 0.2, 0.07, 0.19]);
    const out = [];
    const luna = lunaOtto(nLuna, mx, my, r, k);
    out.push(...luna.punti);
    const largo = x1 - x0, bordo = (x) => Math.min(1, Math.min(x - x0, x1 - x) / (largo * 0.12));
    out.push(...lungo(nOrizzonte, [[x0, or], [x1, or]], (x, y) => P({ x, y: y + gauss() * 0.004, z: 0, lum: 0.34 * bordo(x), gr: 0.75, tinta: T.STELLA })));
    /* la scia è lunga al massimo tre raggi: più giù resta il mare con le sue onde (così è fitta come nell'apertura) */
    const L = or - fondo, LS = Math.min(L * 0.96, r * 3.2), piena = 0.1 + 0.9 * luna.luce, scarto = mx + luna.verso * r * 0.4;
    const trattini = [];
    const righe = 26;
    for (let j = 0; j < righe; j++) {
      const v = j / (righe - 1), y = or - r * 0.08 - Math.pow(v, 1.25) * LS;
      const larga = r * (0.13 + v * 0.95 * (0.45 + 0.55 * luna.luce));
      for (let d = 0; d < 2 + Math.round(v * 6); d++) {
        const c = scarto * (1 - v * 0.15) + mx * v * 0.15 + gauss() * larga * 0.42, l = r * (0.04 + caso() * (0.06 + 0.15 * v));
        trattini.push({ a: [c - l, y], b: [c + l, y], v, luce: caso() });
      }
    }
    const Lt = trattini.reduce((t, d) => t + (d.b[0] - d.a[0]), 0);
    for (const d of trattini) out.push(...lungo(Math.round((nScia * (d.b[0] - d.a[0])) / Lt), [d.a, d.b], (x, y) => P({ x, y: y + gauss() * 0.003, z: 0.1, lum: (0.25 + 0.6 * (1 - d.v)) * piena * (0.6 + 0.8 * d.luce), gr: 0.85, tinta: T.LUNA, anim: d.luce > 0.75 ? A.SCINTILLA : A.FERMO })));
    const onde = 9;
    for (let i = 0; i < onde; i++) {
      const t = Math.pow((i + 1) / (onde + 0.5), 1.5), y0 = or - L * t, fase = caso() * 6.28;
      out.push(...lungo(Math.round(nOnde / onde), Array.from({ length: 90 }, (_, q) => { const x = x0 + (q / 89) * largo; return [x, y0 + Math.sin((x / r) * 4.5 + fase) * r * 0.012]; }), (x, y) => P({ x, y, z: 0, lum: (0.17 - i * 0.011) * bordo(x), gr: 0.75, tinta: T.MARE })));
    }
    return out;
  }
  /* misura una forma e la centra: ingombro w × h (le stelle quasi spente non contano) */
  function misurata(fai) {
    const prova = fai(3000);
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const p of prova) if (p.lum > 0.07) { x0 = Math.min(x0, p.x); x1 = Math.max(x1, p.x); y0 = Math.min(y0, p.y); y1 = Math.max(y1, p.y); }
    const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
    return { fai: (n, ...resto) => sposta(fai(n, ...resto), 1, -cx, -cy), w: x1 - x0 + 0.1, h: y1 - y0 + 0.1, cx, cy };
  }
  const MARE = misurata(lunaAcqua);

  /* ── l'altare: candela, cono d'incenso e drusa su una tovaglia d'oro ── */
  function altare(n) {
    const [nCandela, nIncenso, nCristallo, nTovaglia] = quote(n, [0.29, 0.33, 0.33, 0.05]);
    return [
      ...sposta(candela(nCandela), 0.58, -1.08, -0.11),
      ...sposta(incenso(nIncenso), 0.66, 0.04, -0.04),
      ...sposta(cristallo(nCristallo), 0.56, 1.08, -0.16),
      ...lungo(nTovaglia, [[-1.62, -0.84], [1.62, -0.84]], (x, y) => P({ x, y: y + gauss() * 0.004, z: 0, lum: 0.75, gr: 0.85, tinta: T.ORO })),
    ];
  }

  /* ── la costellazione del segno della stagione, col suo glifo ──
     Stelle principali (ascensione retta in ore, declinazione in gradi, magnitudine) e linee della
     figura; segno tropicale dalla data. Per ora le tre costellazioni di questo periodo: negli altri
     mesi resta la ruota dello zodiaco, finché non ci sono tutte e dodici. */
  const COSTELLAZIONI = {
    Bilancia: {
      stelle: [[14.8479, -16.042, 2.75], [15.2834, -9.383, 2.6], [15.5921, -14.789, 3.9], [15.0678, -25.282, 3.3], [15.617, -28.135, 3.6], [15.6444, -29.778, 3.7]],
      linee: [[3, 0], [0, 1], [1, 2], [2, 0], [2, 4], [4, 5]],
    },
    Scorpione: {
      stelle: [[16.0906, -19.806, 2.6], [16.0056, -22.622, 2.3], [15.9808, -26.114, 2.9], [15.948, -29.214, 3.9], [16.3531, -25.593, 2.9],
        [16.4901, -26.432, 1.0], [16.5981, -28.216, 2.8], [16.8361, -34.293, 2.3], [16.8645, -38.047, 3.0], [16.9097, -42.362, 3.6],
        [17.2026, -43.239, 3.3], [17.622, -42.998, 1.9], [17.7931, -40.127, 3.0], [17.7081, -39.03, 2.4], [17.5601, -37.104, 1.6],
        [17.5127, -37.296, 2.7], [16.2, -19.461, 4.0]],
      linee: [[16, 0], [0, 1], [1, 2], [2, 3], [1, 4], [4, 5], [5, 6], [6, 7], [7, 8], [8, 9], [9, 10], [10, 11], [11, 12], [12, 13], [13, 14], [14, 15]],
      rossa: 5,
    },
    Sagittario: {
      stelle: [[18.0968, -30.424, 3.0], [18.3499, -29.828, 2.7], [18.4029, -34.385, 1.8], [18.4662, -25.422, 2.8], [18.7609, -26.991, 3.2],
        [18.9211, -26.297, 2.0], [19.1157, -27.67, 3.3], [19.0435, -29.88, 2.6]],
      linee: [[0, 1], [0, 2], [1, 2], [1, 3], [3, 4], [4, 1], [2, 7], [7, 4], [4, 5], [5, 6], [6, 7]],
    },
  };
  const semicerchio = (cx, cy, r) => arco(cx, cy, r, r, Math.PI, 0, 16);
  const GLIFI = {
    Scorpione: [
      [[0, 0], [0, 0.26], ...semicerchio(0.07, 0.26, 0.07), [0.14, 0.26], [0.14, 0]],
      [[0.14, 0.26], ...semicerchio(0.21, 0.26, 0.07), [0.28, 0.26], [0.28, 0.06], [0.31, 0.0], [0.37, -0.02], [0.43, 0.01]],
      [[0.43, 0.01], [0.36, 0.03]], [[0.43, 0.01], [0.4, -0.05]],
    ],
    Bilancia: [[[0, 0], [0.42, 0]], [[0, 0.1], [0.13, 0.1], ...semicerchio(0.21, 0.1, 0.08), [0.42, 0.1]]],
    Sagittario: [[[0, 0], [0.4, 0.4]], [[0.4, 0.4], [0.26, 0.4]], [[0.4, 0.4], [0.4, 0.26]], [[0.08, 0.2], [0.2, 0.08]]],
  };
  const INIZI = [['Capricorno', 1, 1], ['Acquario', 1, 20], ['Pesci', 2, 19], ['Ariete', 3, 21], ['Toro', 4, 20], ['Gemelli', 5, 21], ['Cancro', 6, 21],
    ['Leone', 7, 23], ['Vergine', 8, 23], ['Bilancia', 9, 23], ['Scorpione', 10, 23], ['Sagittario', 11, 22], ['Capricorno', 12, 22]];
  const SEGNO = (() => { const d = new Date(), v = (d.getMonth() + 1) * 100 + d.getDate(); let s = INIZI[0][0]; for (const [nome, m, g] of INIZI) if (v >= m * 100 + g) s = nome; return s; })();
  const lunghezzaDi = (linee) => linee.reduce((s, l) => s + l.slice(1).reduce((t, p, i) => t + Math.hypot(p[0] - l[i][0], p[1] - l[i][1]), 0), 0);
  function segno(n) {
    const c = COSTELLAZIONI[SEGNO];
    const [nStelle, nAloni, nFili, nGlifo, nCielo] = quote(n, [0.18, 0.1, 0.32, 0.14, 0.26]);
    const out = [];
    /* il cielo attorno: stelle deboli che scintillano */
    for (let i = 0; i < nCielo; i++) out.push(P({ x: (caso() * 2 - 1) * 1.4, y: (caso() * 2 - 1) * 1.3, z: -0.4, lum: 0.08 + 0.2 * caso() ** 3, gr: 0.75, tinta: T.TENUE, anim: A.SCINTILLA }));
    if (!c) {
      /* la ruota dello zodiaco: dodici case su un cerchio */
      out.push(...lungo(nFili, arco(0, 0, 1.1, 1.1, 0, Math.PI * 2, 180), (x, y) => P({ x, y, lum: 0.6, gr: 0.8, tinta: T.ORO })));
      for (let i = 0; i < nStelle + nAloni + nGlifo; i++) { const a = (((i % 12) + 0.5) / 12) * Math.PI * 2, r = 1.1 - caso() * 0.25; out.push(P({ x: Math.cos(a) * r, y: Math.sin(a) * r, lum: 0.5, gr: 0.8, tinta: T.ORO })); }
      return out;
    }
    const ra0 = c.stelle.reduce((s, x) => s + x[0], 0) / c.stelle.length;
    const de0 = c.stelle.reduce((s, x) => s + x[1], 0) / c.stelle.length;
    const k = Math.cos((de0 * Math.PI) / 180);
    const xy = c.stelle.map(([ra, de]) => [-(ra - ra0) * 15 * k, de - de0]);
    const xs = xy.map((p) => p[0]), ys = xy.map((p) => p[1]);
    const misura = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
    const mx = (Math.max(...xs) + Math.min(...xs)) / 2, my = (Math.max(...ys) + Math.min(...ys)) / 2;
    const s = 2.25 / misura;
    const stelle = xy.map(([x, y], i) => ({ x: 0.18 + (x - mx) * s, y: -0.1 + (y - my) * s, peso: Math.pow(10, -0.4 * (c.stelle[i][2] - 1)), rossa: c.rossa === i }));
    const pesoTot = stelle.reduce((t, x) => t + x.peso, 0);
    const scegli = () => { let r = caso() * pesoTot, j = 0; while (j < stelle.length - 1 && r > stelle[j].peso) { r -= stelle[j].peso; j++; } return stelle[j]; };
    for (let i = 0; i < nStelle; i++) {
      const st = scegli(), rag = 0.008 + 0.012 * Math.sqrt(st.peso);
      out.push(P({ x: st.x + gauss() * rag, y: st.y + gauss() * rag, z: 0.1, lum: 1.5 + st.peso, gr: 1.25 + st.peso * 1.2, tinta: st.rossa ? T.ROSSO : T.STELLA }));
    }
    for (let i = 0; i < nAloni; i++) {
      const st = scegli(), rag = 0.05 * Math.sqrt(st.peso);
      out.push(P({ x: st.x + gauss() * rag, y: st.y + gauss() * rag, z: 0.1, lum: 0.14, gr: 1.4, tinta: st.rossa ? T.ROSSO : T.STELLA }));
    }
    const segmenti = c.linee.map(([a, b]) => [[stelle[a].x, stelle[a].y], [stelle[b].x, stelle[b].y]]);
    const L = lunghezzaDi(segmenti);
    for (const seg of segmenti) {
      const l = lunghezzaDi([seg]);
      out.push(...tratto(Math.round((nFili * l) / L), seg[0][0], seg[0][1], seg[1][0], seg[1][1], 0.004, (x, y) => P({ x, y, lum: 0.5, gr: 0.8, tinta: T.ORO })));
    }
    /* il glifo del segno, in alto a sinistra */
    const glifo = (GLIFI[SEGNO] || []).map((linea) => linea.map(([x, y]) => [-1.32 + x * 1.15, 0.78 + y * 1.15]));
    const Lg = lunghezzaDi(glifo) || 1;
    for (const linea of glifo) out.push(...lungo(Math.round((nGlifo * lunghezzaDi([linea])) / Lg), linea, (x, y) => P({ x: x + gauss() * 0.004, y: y + gauss() * 0.004, z: 0.1, lum: 0.95, gr: 0.85, tinta: T.ORO })));
    return out;
  }

  /* ── l'olio essenziale: la boccetta d'ambra aperta, la pipetta e la goccia che cade ── */
  function olio(n) {
    const BASE = -1.25, SPALLA = -0.02, R = 0.4, COLLO = 0.13, BOCCA = 0.28, PUNTA = 0.6;
    const [nVetro, nEtichetta, nBocca, nPipetta, nBulbo, nGoccia, nRiflesso] = quote(n, [0.47, 0.1, 0.05, 0.08, 0.09, 0.06, 0.15]);
    const largo = (y) => {
      if (y < BASE + 0.08) return R - 0.08 + Math.sqrt(Math.max(0, 0.0064 - (BASE + 0.08 - y) ** 2));
      if (y < SPALLA) return R;
      if (y < SPALLA + 0.2) { const v = (y - SPALLA) / 0.2; return R - (R - COLLO) * (v * v * (3 - 2 * v)); }
      return COLLO;
    };
    const out = [];
    const nellEtichetta = (x, y) => y > -0.86 && y < -0.34 && Math.abs(x) < R - 0.02;
    out.push(...riempi(nVetro, [-R, BASE, R, BOCCA], (x, y) => Math.abs(x) < largo(y) && !nellEtichetta(x, y), (x, y) => {
      const w = largo(y), c = Math.sqrt(Math.max(0, 1 - (x / w) ** 2));
      const riflesso = Math.exp(-(((x + w * 0.58) / 0.035) ** 2)) * (y < SPALLA + 0.1 ? 1 : 0.5) + 0.4 * Math.exp(-(((x - w * 0.82) / 0.025) ** 2));
      const liquido = y < -0.15 ? 0.12 : 0;
      return P({ x, y, z: c * 0.2, lum: 0.3 + 0.42 * c + liquido + 0.8 * riflesso, gr: 0.9, tinta: T.AMBRA });
    }));
    /* l'etichetta: carta bianca col bordo d'oro e una goccia disegnata, senza nomi */
    out.push(...riempi(nEtichetta, [-R, -0.86, R, -0.34], (x, y) => nellEtichetta(x, y), (x, y) => {
      const bordo = Math.abs(x) > R - 0.06 || y < -0.82 || y > -0.38;
      const g = (x / 0.07) ** 2 + ((y + 0.6) / 0.1) ** 2, goccia = Math.abs(Math.sqrt(g) - 1) < 0.18 && y < -0.52;
      return P({ x, y, z: 0.22, lum: bordo || goccia ? 0.75 : 0.22, gr: 0.85, tinta: bordo || goccia ? T.ORO : T.CERA });
    }));
    out.push(...lungo(nBocca, arco(0, BOCCA, COLLO, 0.035, 0, Math.PI * 2, 60), (x, y) => P({ x, y, z: 0.1, lum: 0.9, gr: 0.85, tinta: T.AMBRA })));
    /* la pipetta di vetro, con la punta che si stringe */
    const tubo = [[-0.035, PUNTA + 0.06], [-0.035, 1.02], [0.035, 1.02], [0.035, PUNTA + 0.06], [0.008, PUNTA], [-0.008, PUNTA], [-0.035, PUNTA + 0.06]];
    out.push(...lungo(nPipetta, tubo, (x, y) => P({ x, y, z: 0.15, lum: 0.55, gr: 0.8, tinta: T.CRISTALLO })));
    /* il bulbo di gomma e il suo collarino */
    out.push(...riempi(nBulbo, [-0.1, 1.0, 0.1, 1.3], (x, y) => (x / 0.085) ** 2 + ((y - 1.16) / 0.13) ** 2 < 1 || (Math.abs(x) < 0.06 && y < 1.06), (x, y) => P({ x, y, z: 0.15, lum: 0.22 + 0.45 * Math.exp(-(((x + 0.04) / 0.02) ** 2)), gr: 0.85, tinta: T.LEGNO })));
    /* la goccia: si gonfia sulla punta e cade nella bocca della boccetta (lo fa lo shader) */
    for (let i = 0; i < nGoccia; i++) {
      const v = caso(), w = 0.032 * Math.sqrt(Math.max(0, Math.sin(Math.PI * Math.pow(v, 0.7))));
      out.push(P({ x: (caso() * 2 - 1) * w, y: -v * 0.075, z: 0.2, lum: 1.4, gr: 0.95, tinta: T.AMBRA, anim: A.GOCCIA, fase: 0, p1: PUNTA, p2: PUNTA - BOCCA - 0.04 }));
    }
    /* la luce d'ambra che il vetro getta sul piano */
    out.push(...riempi(nRiflesso, [-0.75, BASE - 0.14, 0.75, BASE + 0.02], (x, y) => (x / 0.72) ** 2 + ((y - (BASE - 0.05)) / 0.08) ** 2 < 1, (x, y) => P({ x, y, z: -0.1, lum: 0.1 + 0.25 * Math.exp(-((x / 0.3) ** 2)), gr: 0.9, tinta: T.AMBRA })));
    return out;
  }

  /* ── la salvia: un mazzetto di salvia bianca legato con lo spago, acceso in punta, col fumo ── */
  function salvia(n) {
    const A0 = [-0.55, -1.15], B = [0.36, 0.33];
    const L = Math.hypot(B[0] - A0[0], B[1] - A0[1]), d = [(B[0] - A0[0]) / L, (B[1] - A0[1]) / L], nr = [-d[1], d[0]];
    const largo = (u) => 0.055 + 0.105 * Math.pow(Math.sin(Math.PI * Math.min(1, (u / L) * 0.92)), 0.65) + 0.04 * (u / L);
    const punto = (u, v) => [A0[0] + d[0] * u + nr[0] * v, A0[1] + d[1] * u + nr[1] * v];
    const [nFoglie, nSpago, nCarbone, nBrace, nFumo] = quote(n, [0.47, 0.09, 0.04, 0.03, 0.37]);
    const out = [];
    for (let i = 0; i < nFoglie; i++) {
      const u = caso() * (L - 0.1), w = largo(u), v = (caso() * 2 - 1) * w;
      const foglia = 0.5 + 0.5 * Math.sin(u * 38 + v * 55 + Math.sin(v * 30) * 2);
      const [x, y] = punto(u, v);
      out.push(P({ x, y, z: (1 - Math.abs(v) / w) * 0.15, lum: 0.3 + 0.42 * foglia + 0.28 * (1 - Math.abs(v) / w), gr: 0.9, tinta: T.SALVIA }));
    }
    /* lo spago avvolto a spirale, con un incrocio */
    for (let i = 0; i < nSpago; i++) {
      const u = 0.12 + caso() * (L - 0.4), w = largo(u), v = (caso() * 2 - 1) * w;
      const giro = (u * 11 + (v / w) * 0.35 * (i % 2 ? 1 : -1)) % 1;
      const uu = u - giro / 11;
      const [x, y] = punto(uu, v);
      out.push(P({ x, y, z: 0.18, lum: 0.95, gr: 0.85, tinta: T.CERA }));
    }
    /* la punta bruciata e la brace */
    for (let i = 0; i < nCarbone; i++) { const u = L - 0.1 + caso() * 0.08, w = largo(u), [x, y] = punto(u, (caso() * 2 - 1) * w); out.push(P({ x, y, z: 0.1, lum: 0.12, gr: 0.85, tinta: T.TENUE })); }
    for (let i = 0; i < nBrace; i++) { const u = L - 0.03 + caso() * 0.03, w = largo(u) * 0.9, [x, y] = punto(u, (caso() * 2 - 1) * w); out.push(P({ x, y, z: 0.12, lum: 1.6, gr: 1.0, tinta: T.FIAMMA, anim: A.FIAMMA, p1: y - 0.02, p2: 0.06 })); }
    /* il fumo sale dritto, qualunque sia l'inclinazione del mazzetto */
    for (let i = 0; i < nFumo; i++) out.push(P({ x: B[0] + gauss() * 0.03, y: B[1] + 0.02, z: 0.1, lum: 0.5, gr: 1.0, tinta: T.FUMO, anim: A.FUMO, p1: 1.08 }));
    return out;
  }

  /* ── il sale dell'Himalaya: il portacandele di sale rosa, acceso da un lumino dentro ── */
  function sale(n) {
    const CY = -0.48, RX = 0.86, RY = 0.64, FONDO = -1.04, CAVO = [0, 0.1, 0.27, 0.06];
    const bordoR = (a) => 1 + 0.08 * Math.sin(3 * a + 1) + 0.06 * Math.sin(5 * a + 2) + 0.04 * Math.sin(9 * a);
    const dentroRoccia = (x, y) => {
      if (y < FONDO) return false;
      const a = Math.atan2((y - CY) / RY, x / RX), r = Math.hypot(x / RX, (y - CY) / RY);
      return r < bordoR(a);
    };
    const nelCavo = (x, y) => (x / CAVO[2]) ** 2 + ((y - CAVO[1]) / CAVO[3]) ** 2 < 1;
    const [nRoccia, nBordo, nLumino, nFiamma, nAlone, nLuce] = quote(n, [0.66, 0.06, 0.03, 0.09, 0.06, 0.1]);
    const out = [];
    const faccia = (x, y) => { const fx = Math.floor(x * 6 + Math.sin(y * 9)), fy = Math.floor(y * 6 + Math.sin(x * 7)); return (Math.sin(fx * 12.9898 + fy * 78.233) * 43758.5453) % 1; };
    out.push(...riempi(nRoccia, [-RX * 1.2, FONDO, RX * 1.2, CY + RY * 1.2], (x, y) => dentroRoccia(x, y) && !nelCavo(x, y), (x, y) => {
      /* la luce viene dal lumino nel cavo: più forte appena sotto, e la pietra la lascia passare a chiazze */
      const g = Math.exp(-((x * x * 0.7 + (y + 0.25) ** 2) / 0.38));
      return P({ x, y, z: 0.1, lum: 0.22 + 1.9 * g + 0.25 * Math.abs(faccia(x, y)) * (0.4 + g), gr: 0.9 + 0.55 * g, tinta: T.SALE, anim: A.BRILLA, fase: 0.3 + caso() * 0.1 });
    }));
    const contorno = arco(0, 0, 1, 1, 0, Math.PI * 2, 160).map(([cx, cy]) => { const a = Math.atan2(cy, cx), r = bordoR(a); return [cx * r * RX, Math.max(FONDO, CY + cy * r * RY)]; });
    out.push(...lungo(nBordo, contorno, (x, y) => P({ x, y, z: 0.1, lum: 0.6, gr: 0.85, tinta: T.SALE, anim: A.BRILLA, fase: 0.35 })));
    out.push(...lungo(nLumino, arco(CAVO[0], CAVO[1], CAVO[2] * 0.8, CAVO[3] * 0.8, 0, Math.PI * 2, 48), (x, y) => P({ x, y, z: 0.15, lum: 0.7, gr: 0.8, tinta: T.CERA })));
    const F0 = CAVO[1] + 0.02, FH = 0.36;
    out.push(...riempi(nFiamma, [-0.09, F0, 0.09, F0 + FH], (x, y) => { const u = (y - F0) / FH, w = 0.075 * Math.pow(Math.sin(Math.PI * Math.pow(u, 0.62)), 0.85); return Math.abs(x) < w ? { u, w } : null; }, (x, y, { u, w }) => P({ x, y, z: 0.2, lum: 0.9 + 1.2 * (1 - Math.abs(x) / w) * (1 - u * 0.5), gr: 0.95, tinta: T.FIAMMA, anim: A.FIAMMA, p1: F0, p2: FH })));
    for (let i = 0; i < nAlone; i++) { const a = caso() * 6.283, r = Math.abs(gauss()) * 0.22; out.push(P({ x: Math.cos(a) * r, y: F0 + 0.15 + Math.sin(a) * r, z: 0.2, lum: 0.1, gr: 1.5, tinta: T.FIAMMA, anim: A.FIAMMA, p1: F0, p2: FH })); }
    /* la luce calda che la roccia getta sul piano */
    out.push(...riempi(nLuce, [-1.25, FONDO - 0.16, 1.25, FONDO + 0.04], (x, y) => (x / 1.2) ** 2 + ((y - (FONDO - 0.05)) / 0.1) ** 2 < 1, (x, y) => P({ x, y, z: -0.1, lum: 0.08 + 0.24 * Math.exp(-((x / 0.5) ** 2)), gr: 0.9, tinta: T.SALE, anim: A.BRILLA, fase: 0.3 })));
    return out;
  }

  /* ── l'acchiappasogni: il cerchio fasciato, la rete a spirale, la perlina, tre piume che oscillano ── */
  function acchiappasogni(n) {
    const C = [0, 0.42], R = 0.66;
    const [nCerchio, nRete, nPerlina, nFili, nPerline, nPiume] = quote(n, [0.2, 0.3, 0.03, 0.06, 0.05, 0.36]);
    const out = [];
    for (let i = 0; i < nCerchio; i++) {
      const a = caso() * Math.PI * 2, r = R + gauss() * 0.012;
      out.push(P({ x: C[0] + Math.cos(a) * r, y: C[1] + Math.sin(a) * r, z: 0.1, lum: Math.sin(a * 60) > 0 ? 0.75 : 0.4, gr: 0.85, tinta: T.LEGNO }));
    }
    /* la rete: poligoni a otto lati che si stringono, ognuno appeso a metà dei lati di quello fuori */
    const V = (k, i) => { const r = R * 0.98 * Math.pow(0.78, k), a = Math.PI / 2 + ((i + k * 0.5) / 8) * Math.PI * 2; return [C[0] + Math.cos(a) * r, C[1] + Math.sin(a) * r]; };
    const fili = [];
    for (let k = 0; k < 6; k++) for (let i = 0; i < 8; i++) fili.push([V(k, i), V(k + 1, i)], [V(k + 1, i), V(k, i + 1)]);
    const Lr = lunghezzaDi(fili);
    for (const f of fili) out.push(...lungo(Math.round((nRete * lunghezzaDi([f])) / Lr), f, (x, y) => P({ x, y, z: 0.05, lum: 0.45, gr: 0.75, tinta: T.CERA })));
    for (let i = 0; i < nPerlina; i++) { const a = caso() * 6.283, r = Math.sqrt(caso()) * 0.035; out.push(P({ x: C[0] + Math.cos(a) * r, y: C[1] + Math.sin(a) * r, z: 0.12, lum: 1.1, gr: 0.9, tinta: T.TURCHESE })); }
    /* tre fili appesi in basso, ognuno con una perlina e una piuma: oscillano attorno all'aggancio */
    const appesi = [[-115, 0.3], [-90, 0.44], [-65, 0.3]];
    appesi.forEach(([gradi, lung], j) => {
      const a = (gradi * Math.PI) / 180, ax = C[0] + Math.cos(a) * R, ay = C[1] + Math.sin(a) * R, fase = j * 0.31;
      const G = (o) => P({ anim: A.PIUMA, fase, p1: ax, p2: ay, ...o });
      out.push(...tratto(Math.round(nFili / 3), 0, 0, 0, -lung, 0.003, (x, y) => G({ x, y, z: 0.1, lum: 0.45, gr: 0.75, tinta: T.CERA })));
      for (let i = 0; i < Math.round(nPerline / 3); i++) { const b = caso() * 6.283, r = Math.sqrt(caso()) * 0.03; out.push(G({ x: Math.cos(b) * r, y: -lung * 0.55 + Math.sin(b) * r, z: 0.12, lum: 0.95, gr: 0.85, tinta: T.TURCHESE })); }
      /* la piuma: rachide al centro, vessillo che si allarga e si chiude in punta, barbe a lisca */
      const P0 = -lung, H = 0.5;
      for (let i = 0; i < Math.round(nPiume / 3); i++) {
        const t = caso(), w = 0.07 * Math.pow(Math.sin(Math.PI * Math.pow(t, 0.75)), 0.9), x = (caso() * 2 - 1) * w;
        const barbe = Math.sin(t * 70 + Math.abs(x) * 90) > 0.1 ? 1 : 0.55;
        const rachide = Math.exp(-((x / 0.006) ** 2));
        out.push(G({ x: x + 0.05 * t * t, y: P0 - t * H, z: 0.1, lum: (0.28 + 0.3 * barbe) * (1 - Math.abs(x) / (w + 0.001) * 0.35) + 0.5 * rachide, gr: 0.85, tinta: T.PIUMA }));
      }
    });
    return out;
  }

  /* quanto spazio prende ogni forma (per farla stare nel riquadro di 3,0 × 2,8) */
  const FORME = {
    altare: { fai: altare, w: 3.45, h: 2.55 },
    tappetino: { fai: tappetino, w: 2.7, h: 2.8 },
    luna: { fai: (n) => MARE.fai(n), w: MARE.w, h: MARE.h },
    candela: { fai: candela, w: 1.4, h: 2.4 },
    incenso: { fai: incenso, w: 1.5, h: 2.95 },
    cristallo: { fai: cristallo, w: 2.2, h: 2.45 },
    campana: { fai: campana, w: 3.0, h: 2.2 },
    tarocco: { fai: tarocco, w: 1.75, h: 2.75 },
    mala: { fai: mala, w: 2.1, h: 2.8 },
    segno: { fai: segno, w: 2.9, h: 2.7 },
    olio: { fai: olio, w: 1.6, h: 2.7 },
    salvia: { fai: salvia, w: 1.7, h: 2.75 },
    sale: { fai: sale, w: 2.6, h: 2.0 },
    acchiappasogni: { fai: acchiappasogni, w: 1.6, h: 2.5 },
  };

  /* Una forma pronta per il motore: esattamente n stelle, già scalata nel riquadro comune. */
  function forma(nome, n) {
    const f = FORME[nome];
    const lista = f.fai(n);
    while (lista.length < n) { const p = lista[(caso() * lista.length) | 0]; lista.push({ ...p, x: p.x + gauss() * 0.004, y: p.y + gauss() * 0.004 }); }
    /* se ne sono venute troppe se ne tolgono a caso, non in coda: in coda c'è l'ultima parte disegnata */
    for (let i = lista.length - 1; i > 0 && lista.length > n; i--) { const j = (caso() * (i + 1)) | 0; [lista[i], lista[j]] = [lista[j], lista[i]]; }
    lista.length = n;
    const k = Math.min(3.0 / f.w, 2.8 / f.h);
    if (k !== 1 && nome !== 'mala') sposta(lista, k, 0, 0);
    /* centra in verticale nel riquadro le forme più basse */
    return lista;
  }

  /* la luna sul mare per la pagina della luna (assets/kg-luna.js): una delle otto fasi, centrata; dove sta
     il disco, per l'alone; l'indice e l'angolo di ogni fase */
  const mare = {
    fai: (n, k) => MARE.fai(n, k), w: MARE.w, h: MARE.h,
    luna: { x: LUNA_MARE.x - MARE.cx, y: LUNA_MARE.y - MARE.cy, r: LUNA_MARE.r },
    indice: indiceFase, angolo: (k) => (FASI_OTTO[k][1] / SINODICO) * Math.PI * 2,
    scena: scenaMare,
  };
  window.KG_FORME = { forma, nomi: Object.keys(FORME), T, A, luna: OGGI, segno: SEGNO, mare };
})();
